// ── PositionTracker card-order Lock mode (issue #97) ─────────────
//
// During fast markets the live sorts (queue/size/velocity, and the Default
// event-start resort) reorder cards while a trader is mid-cancel, so a card
// jumps out from under the cursor. Lock mode captures the current sorted
// order and holds it: cards stop moving while their CONTENTS keep updating
// in place. Ordering is frozen, nothing else — no effect on data, placement,
// cancellation, or accounting (those read the live rows regardless of order).

/**
 * Reorder live-sorted rows to honour a frozen order.
 *
 * `frozenKeys` is the identity order snapshotted when Lock was enabled
 * (`liveRows.map(keyOf)` at that moment). Returns a NEW array of the SAME live
 * row objects (so every card keeps rendering fresh content) reordered as:
 *
 *  1. Rows whose key is in `frozenKeys` first, in `frozenKeys` order. Keys that
 *     have since disappeared are simply skipped, so survivors keep their
 *     relative locked positions — a card leaving never reshuffles the rest.
 *  2. Rows whose key is NOT in `frozenKeys` (cards that appeared after the
 *     lock) after the frozen block, in their current live-sorted order. New
 *     cards deterministically append at the tail; they never shove a locked
 *     card out of place.
 *
 * `frozenKeys == null` means unlocked — the live order is returned verbatim
 * (a copy), so unlocking immediately restores the active sort.
 *
 * Pure and allocation-cheap; safe to call every render.
 */
/**
 * Which ordering a PositionTracker surface should render for the active view.
 *
 * Lock is a CARD-ordering concept (PR #102 review): the card / compact / mini
 * views honor the frozen order, but the **table always renders the live sort**.
 * The Lock control is only shown in the card-family views, so a frozen table
 * would strand the trader with no visible way to unlock. Keeping the table on
 * the live rows means switching to Table always reflects the current sort, and
 * the lock simply resumes when the trader returns to a card view.
 *
 * `isTableView` → `liveRows`; anything else → `lockedRows`.
 */
export function orderingForView<T>(
  isTableView: boolean,
  lockedRows: T[],
  liveRows: T[],
): T[] {
  return isTableView ? liveRows : lockedRows;
}

export function applyLockedOrder<T>(
  liveRows: readonly T[],
  frozenKeys: readonly string[] | null,
  keyOf: (row: T) => string,
): T[] {
  if (frozenKeys == null) return liveRows.slice();

  // key → locked rank. First occurrence wins (keys are unique per surface;
  // the guard just makes a duplicated key deterministic rather than NaN).
  const rank = new Map<string, number>();
  for (let i = 0; i < frozenKeys.length; i++) {
    if (!rank.has(frozenKeys[i])) rank.set(frozenKeys[i], i);
  }

  const frozen: T[] = [];
  const fresh: T[] = [];
  for (const row of liveRows) {
    (rank.has(keyOf(row)) ? frozen : fresh).push(row);
  }
  // Restore the captured order for the frozen block; `fresh` keeps live order.
  frozen.sort((a, b) => (rank.get(keyOf(a)) ?? 0) - (rank.get(keyOf(b)) ?? 0));
  return frozen.concat(fresh);
}
